import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { processTerm } from "../../../api/content/processTerms";
import ErrorNotice from "../../../errors/ErrorNotice";
import type { UiError } from "../../../errors/uiError";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { classText } from "../../workflows/inputCards/typeWords";
import { routeFitOf, type TurnRoute } from "../hosting/routeChoices";
import { gapsText } from "../hosting/routeFitText";
import LlmPicker, { type LlmChoice } from "./LlmPicker";
import type { RunsWithOpener } from "./runsWithOpener";
import { llmLabel, notOfferedText, unavailabilityOn, withoutEffort, type ConnectorOptions } from "./llmOptions";

interface RunsWithRowProps {
  /** The route it runs on; `null` before one is chosen. */
  route: TurnRoute | null;
  /** The PC it runs on, among `choices`; `null` before one is chosen. */
  connectorId: string | null;
  /** Said after the route, such as that it is the Thread's default; `null` for nothing. */
  note: string | null;
  /** The PCs and the server's route options it is chosen from; empty before they are read or where none is offered. */
  choices: readonly ConnectorOptions[];
  /** Whether `choices` are read: before, the row doesn't judge the route. */
  read: boolean;
  onRefresh: () => void;
  /** Whether this person may change it here. */
  mayPick: boolean;
  /** A step's own route that can go back to the Thread's default. */
  own: boolean;
  onChoose: (choice: LlmChoice | null) => void;
  /**
   * Shown instead of the row while it isn't `own` and the picker is closed, for a step that runs
   * with the default: the button that opens the picker for its own LLM. The row owns whether the
   * picker is open, so Cancel brings the button back (Sol R2 S1).
   */
  askLabel?: string | null;
  /** Said under it when the person may choose but no PC offers anything yet, such as "Connect this PC". */
  noChoices?: React.ReactNode;
  /** Where it registers how to open it, for places elsewhere that lead here (the Thread's default, S12). */
  opener?: RunsWithOpener | null;
  busy: boolean;
  error: UiError | null;
}

/**
 * "Runs with" (F106, Design C): what an agent step, or a Thread by default, runs with, labelled
 * by the ontology (`dmwp:runsWith`), with the one LLM picker behind Change. Its owner holds the
 * value: a Thread's settings (`RunsWithCard`) or a start's draft (`StartRoutes`). An LLM the
 * chosen PC no longer offers stops it: the row says so and opens the picker; nothing falls back.
 */
export default function RunsWithRow({ route, connectorId, note, choices, read, onRefresh, mayPick, own, onChoose, askLabel = null, noChoices = null, opener = null, busy, error }: RunsWithRowProps) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(
    () =>
      opener?.register(() => {
        rowRef.current?.scrollIntoView({ block: "nearest" });
        setPicking(true);
      }),
    [opener],
  );
  const shown = choices.find((choice) => choice.connector.connectorId === connectorId);
  const connector = shown?.connector;
  const unavailable = route === null || !read ? null : unavailabilityOn(route, shown);
  const offered = unavailable === null;
  const fit = route === null ? null : routeFitOf(shown?.listing.options ?? null, withoutEffort(route));
  const gaps = fit !== null && (fit.kind === "lacks" || fit.kind === "uncertain") ? gapsText(t, fit.gaps) : null;
  const canPick = mayPick && choices.length > 0;
  const open = canPick && (picking || !offered);
  const choose = (choice: LlmChoice | null) => {
    onChoose(choice);
    setPicking(false);
  };
  // A step on the default says nothing more than the button that gives it its own (W-07).
  if (askLabel !== null && !own && !picking) {
    return canPick ? <AppButton variant="ghost" size="xs" onClick={() => { setPicking(true); }}>{askLabel}</AppButton> : null;
  }

  return (
    <div ref={rowRef} className="space-y-1.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <span className="app-label">{classText(processTerm("runsWith"), t)}</span>
        <span className="min-w-0 flex-1">
          {route === null ? t("threads.llm.notChosen") : llmLabel(route, connector, t)}
          {note === null || route === null ? null : <span className={`${FORM_HELP_TEXT_CLASS} ml-2`}>{note}</span>}
        </span>
        {canPick && !open ? (
          <AppButton variant={route === null ? "primary" : "secondary"} size="xs" onClick={() => { setPicking(true); }}>{t(route === null ? "threads.llm.choose" : "threads.header.change")}</AppButton>
        ) : null}
      </div>
      {mayPick && read && choices.length === 0 ? noChoices : null}
      {unavailable !== null ? (
        <p className="app-status-text-warning text-xs">{notOfferedText(connector?.name ?? t("threads.header.yourPc"), unavailable, t)}</p>
      ) : gaps !== null ? (
        <p className={fit?.kind === "lacks" ? "app-status-text-warning text-xs" : `${FORM_HELP_TEXT_CLASS} text-xs`}>{gaps}</p>
      ) : null}
      {error === null ? null : <ErrorNotice error={error} compact />}
      {open ? (
        <div className="app-card space-y-2 p-3">
          <LlmPicker
            choices={choices}
            onRefresh={onRefresh}
            value={route === null || connector === undefined ? null : { connectorId: connector.connectorId, route }}
            onChoose={choose}
            footer={
              <div className="flex justify-end gap-2">
                {own ? (
                  <AppButton variant="secondary" size="xs" disabled={busy} onClick={() => { choose(null); }}>{t("threads.llm.useThreadDefault")}</AppButton>
                ) : null}
                {offered ? <AppButton variant="secondary" size="xs" onClick={() => { setPicking(false); }}>{t("common.cancel")}</AppButton> : null}
              </div>
            }
          />
        </div>
      ) : null}
    </div>
  );
}
